<template>
  <v-container fluid>
    <v-snackbar v-model="showSnackbar" :color="snackbar.color" transition="slide-y-reverse-transition">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showSnackbar = false"
        >
          {{getTranslated('_close')}}
        </v-btn>
      </template>
    </v-snackbar>

    <ConfirmationDialog
      :showDialog="showSmartDisplayConfirmationDialog"
      :text="getTranslated('_smartDisplayWillDeleted')"
      @accept="deleteSmartDisplay"
      @cancel="cancelSmartDisplayDeleteDialog"
    ></ConfirmationDialog>

    <ConfirmationDialog
      :showDialog="showDashboardConfirmationDialog"
      :text="getTranslated('_dashboardWillDeleted')"
      @accept="deleteDashboard"
      @cancel="cancelDashBoardDeleteDialog"
    ></ConfirmationDialog>

    <SmartDisplayEditionDialog
      :openDialog="showSmartDisplayEditionDialog"
      @input="showSmartDisplayEditionDialog=false"
      @saved="notify"
      :translations="translations"
    >
    </SmartDisplayEditionDialog>

    <DashboardEditionDialog
      :openDialog="showDashboardEditionDialog"
      :selectedBoardId="getDashboardItem().board.id"
      @input="showDashboardEditionDialog=false"
      @saved="notify"
      :translations="translations"
    >
    </DashboardEditionDialog>

    <v-skeleton-loader
      :loading="tableLoading"
      type="table"
      class="elevation-1 my-12"
      transition="fade-transition"
    >
      <v-window v-model="step" class="my-12" touchless>
        <v-window-item :value="1">
          <div>
            <v-data-table
              :headers="headersSmartDisplays"
              :items="smartDisplays"
              must-sort
              :hide-default-footer="smartDisplays.length <= 10"
              width="100%"
            >
            <template v-slot:top>
              <v-toolbar flat>
                <v-toolbar-title>Smart Displays</v-toolbar-title>
                <v-divider
                  class="mx-4"
                  inset
                  vertical
                ></v-divider>
                <div class="flex-grow-1"></div>
                <v-btn color="primary" dark class="mb-2" icon x-large @click.stop="showSmartDisplayEditionDialog=true" v-if="permits.writePermit">
                  <v-icon>
                    add_circle
                  </v-icon>
                </v-btn>
              </v-toolbar>
            </template>

            <template #item.board.type.id="{ item }">
              <v-icon :class="getBoardTypeIcon(item.board.type.id) == 'view_agenda' ? 'rotate-90' : ''">{{getBoardTypeIcon(item.board.type.id)}}</v-icon>
            </template>

            <template #item.smartDisplayName="{ item }">
              {{item.smartDisplayName}}
              <v-btn :href="base_url + '/' + tenant + '/' + item.id" target="_blank" icon color="primary" class="ml-1">
                <v-icon x-small>fa-external-link-alt</v-icon>
              </v-btn>
            </template>

            <template #item.action="{ item }">
              <v-icon
                small
                class="mr-1"
                @click="editSmartDisplay(item)"
              >
                edit
              </v-icon>
              <span class="text-overline" style="cursor: pointer" @click="editSmartDisplay(item)">{{getTranslated('_edit')}}</span>
              <v-icon
                small
                class="mr-1 ml-4"
                @click="openSmartDisplayDeleteDialog(item)"
              >
                delete
              </v-icon>
              <span class="text-overline" style="cursor: pointer" @click="openSmartDisplayDeleteDialog(item)">{{getTranslated('_delete')}}</span>
            </template>

            <template #item.next-Step="{ item }">
              <v-btn icon @click="callDashboardScreen(item)"><v-icon>keyboard_arrow_right</v-icon></v-btn>
            </template>

            <template #no-data>
              <label>{{getTranslated('_smartDisplayNoData')}}</label>
            </template>

            </v-data-table>
          </div>
        </v-window-item>
        <v-window-item :value="2">
          <v-data-table
            :headers="headersDashboards"
            :items="getDashboardItem().board.dashboards"
            :sort-by="['dashboardOrder']"
            :items-per-page="20"
            must-sort
            class="elevation-1"
            hide-default-footer
            width="100%"
          >
          <template v-slot:top>
          <v-toolbar dense flat>
            <v-btn icon @click="callSmartDisplayScreen()"><v-icon>keyboard_arrow_left</v-icon></v-btn>
            <v-toolbar-title>Dashboards: {{getDashboardItem().board.dashboards.length}}/{{getDashboardItem().board.type.maxDashboards}}</v-toolbar-title>
            <v-spacer></v-spacer>
            <a :href="base_url + '/' + tenant + '/' + getDashboardItem().board.smartDisplayId" target="_blank">
              {{base_url}}/{{tenant}}/{{getDashboardItem().board.smartDisplayId}}
            </a>
            <div class="flex-grow-1"></div>
            <v-tooltip top :disabled="!disableAddDashboardButton(getDashboardItem().board.dashboards.length, getDashboardItem().board.type.maxDashboards)" transition="slide-y-reverse-transition">
              <template v-slot:activator="{ on }">
                <div v-on="on">
                  <v-btn v-if="dashboardsPermits.writePermit" color="primary" class="mt-2" x-large icon style="padding: 0px" @click.stop="showDashboardEditionDialog=true" :disabled="disableAddDashboardButton(getDashboardItem().board.dashboards.length, getDashboardItem().board.type.maxDashboards)">
                    <v-icon>
                      add_circle
                    </v-icon>
                  </v-btn>
                </div>
              </template>
              <span>{{getTranslated('_limitDashboardAlert')}}</span>
            </v-tooltip>
          </v-toolbar>
          </template>
          <template #item.action="{ item }">
            <v-icon
              small
              class="mr-1"
              @click="editDashboard(item)"
            >
              edit
            </v-icon>
            <span class="text-overline" style="cursor: pointer" @click="editDashboard(item)">{{getTranslated('_edit')}}</span>
            <v-icon
              small
              class="mr-1 ml-4"
              @click="openDashBoardDeleteDialog(item)"
            >
              delete
            </v-icon>
            <span class="text-overline" style="cursor: pointer" @click="openDashBoardDeleteDialog(item)">{{getTranslated('_delete')}}</span>
          </template>

          <template #item.value="{ item }">
            {{item.value}}
          </template>

          <template #no-data>
            <label>{{getTranslated('_dashboardNoData')}}</label>
          </template>

          </v-data-table>
        </v-window-item>
      </v-window>
    </v-skeleton-loader>
  </v-container>

</template>

<script>
import { mapState, mapActions } from 'vuex'
import { actions, actionsForDashboards } from '../store/types/smartDisplayTypes.js'
import smartDisplayService from '../services/smartDisplayService'
import dashboardService from '../services/dashboardService'
import screenService from '../services/ScreenService.js'
import translationsService from '../services/TranslationsService'
const ConfirmationDialog = () => import('../components/ConfirmationDialog.vue')
const SmartDisplayEditionDialog = () => import('../components/SmartDisplayEditionDialog.vue')
const DashboardEditionDialog = () => import('../components/DasboardEditionDialog.vue')
export default {
  components: {
    ConfirmationDialog,
    SmartDisplayEditionDialog,
    DashboardEditionDialog
  },
  data () {
    return {
      step: 1,
      tableLoading: true,
      expanded: [],
      smartDisplayId: '',
      showSmartDisplayConfirmationDialog: false,
      showDashboardConfirmationDialog: false,
      smartDisplayToDelete: '',
      dashBoardToDelete: '',
      showSmartDisplayEditionDialog: false,
      showDashboardEditionDialog: false,
      snackbar: {
        color: '',
        message: ''
      },
      showSnackbar: false,
      base_url: '',
      tenant: '',
      permits: '',
      dashboardsPermits: '',
      translations: [
        { key: '_smartDisplayDeleted', translation: 'Smart Display Deleted' },
        { key: '_smartDisplayDeletedError', translation: 'Error deleting Smart Display' },
        { key: '_dashboardDeleted', translation: 'Dashboard Deleted' },
        { key: '_dashboardDeletedError', translation: 'Error deleting Dashboard' },
        { key: '_smartDisplayWillDeleted', translation: 'Smart Display will be deleted' },
        { key: '_dashboardWillDeleted', translation: 'Dashboard will be deleted' },
        { key: '_smartDisplayNoData', translation: 'No Smart Displays were found' },
        { key: '_dashboardNoData', translation: 'No Dashboards were found' },
        { key: '_name', translation: 'Name' },
        { key: '_url', translation: 'URL' },
        { key: '_screenTime', translation: 'Screen Time' },
        { key: '_order', translation: 'Order' },
        { key: '_addDashboard', translation: 'Add Dasbhoard' },
        { key: '_addSmartDisplay', translation: 'Add Smart Display' },
        { key: '_editDashboard', translation: 'Edit Dasbhoard' },
        { key: '_editSmartDisplay', translation: 'Edit Smart Display' },
        { key: '_wmsInstance', translation: 'WMS Instance' },
        { key: '_board', translation: 'Board' },
        { key: '_type', translation: 'Type' },
        { key: '_close', translation: 'Close' },
        { key: '_cancel', translation: 'Cancel' },
        { key: '_accept', translation: 'Accept' },
        { key: '_save', translation: 'Save' },
        { key: '_limitDashboardAlert', translation: 'You cannot add more dashboards since you have reached the maximum' },
        { key: '_saveRequiredValuesAlert', translation: 'To save enter the required values' },
        { key: '_edit', translation: 'Edit' },
        { key: '_delete', translation: 'Delete' },
        { key: '_required', translation: 'Required' }
      ]
    }
  },
  computed: {
    ...mapState({
      headersSmartDisplays: state => state.smartDisplays.headersSmartDisplays,
      headersDashboards: state => state.smartDisplays.headersDashboards,
      smartDisplays: state => state.smartDisplays.smartDisplaysList,
      dashboards: state => state.dashboards.dashboardsList
    })
  },
  methods: {
    ...mapActions({
      saveCurrentSmartDisplay: actions.SAVE_CURRENT_SMARTDISPLAY,
      getSmartDisplays: actions.GET_SMARTDISPLAYS,
      getHeadersDashboards: actions.GET_HEADERS_DASHBOARDS,
      getHeadersSmartDisplays: actions.GET_HEADERS_SMARTDISPLAYS,
      saveCurrentDashboard: actionsForDashboards.SAVE_CURRENT_DASHBOARD
    }),
    async initialize () {
      if (this.$root.$data.user.profile !== undefined) {
        this.permits = await screenService.getScreenPermits('Smd_SmartDisplays', this.$root.$data.user.profile['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']).catch((e) => this.errorManagement(e))
        this.permits = this.permits.data
        this.dashboardsPermits = await screenService.getScreenPermits('Smd_Dashboards', this.$root.$data.user.profile['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']).catch((e) => this.errorManagement(e))
        this.dashboardsPermits = this.dashboardsPermits.data
        await this.getHeadersDashboards('Smd_Dashboards').catch((e) => this.errorManagement(e))
        await this.getHeadersSmartDisplays('Smd_SmartDisplays').catch((e) => this.errorManagement(e))
        await this.getSmartDisplays().catch((e) => this.errorManagement(e))
        await this.getTranslations().catch((e) => this.errorManagement(e))
        // Añadimos el valor width con 50% porque el campo "value" es muy grande (es un caso especial)
        this.headersDashboards.forEach(element => {
          if (element.value === 'value') {
            element.width = '50%'
          }
        })
      }
    },
    callDashboardScreen (item) {
      this.smartDisplayId = item.id
      this.step++
    },
    callSmartDisplayScreen () {
      this.step--
    },
    async deleteSmartDisplay (item) {
      await smartDisplayService.deleteSmartDisplay(this.smartDisplayToDelete).then(res => {
        this.cancelSmartDisplayDeleteDialog()
        this.getSmartDisplays()
        this.snackbar.color = res.request.status === 200 ? 'green' : 'red'
        this.snackbar.message = res.request.status === 200 ? this.getTranslated('_smartDisplayDeleted') : this.getTranslated('_smartDisplayDeletedError')
        this.showSnackbar = true
      }).catch((e) => this.errorManagement(e))
    },
    async deleteDashboard (item) {
      await dashboardService.deleteDashboard(this.dashBoardToDelete).then(res => {
        this.cancelDashBoardDeleteDialog()
        this.getSmartDisplays()
        this.snackbar.color = res.request.status === 200 ? 'green' : 'red'
        this.snackbar.message = res.request.status === 200 ? this.getTranslated('_dashboardDeleted') : this.getTranslated('_dashboardDeletedError')
        this.showSnackbar = true
      }).catch((e) => this.errorManagement(e))
    },
    cancelSmartDisplayDeleteDialog () {
      this.showSmartDisplayConfirmationDialog = false
      this.smartDisplayToDelete = ''
    },
    openSmartDisplayDeleteDialog (item) {
      this.showSmartDisplayConfirmationDialog = true
      this.smartDisplayToDelete = item.id
    },
    cancelDashBoardDeleteDialog () {
      this.showDashboardConfirmationDialog = false
      this.dashBoardToDelete = ''
    },
    openDashBoardDeleteDialog (item) {
      this.showDashboardConfirmationDialog = true
      this.dashBoardToDelete = item.id
    },
    notify (notification) {
      this.snackbar.color = notification.color
      this.snackbar.message = notification.message
      this.showSnackbar = true
    },
    editSmartDisplay (item) {
      this.saveCurrentSmartDisplay(item)
      this.showSmartDisplayEditionDialog = true
    },
    editDashboard (item) {
      this.saveCurrentDashboard(item)
      this.showDashboardEditionDialog = true
    },
    getDashboardItem () {
      return this.smartDisplayId === '' ? { board: { id: '' } } : this.smartDisplays.find(x => x.id === this.smartDisplayId)
    },
    getBoardTypeIcon (boardTypeId) {
      var icon
      switch (boardTypeId) {
        case 1:
          icon = 'web_asset'
          break
        case 2:
          icon = 'view_agenda'
          break
        case 3:
          icon = 'view_carousel'
          break
        default:
          icon = 'error'
          break
      }
      return icon
    },
    disableAddDashboardButton (currentDashBoards, maxDashboards) {
      return currentDashBoards >= maxDashboards
    },
    async getTranslations () {
      if (this.$root.$data.user.profile !== undefined) {
        await translationsService.get('Smd_SmartDisplays', this.$root.$data.user.profile['customclaim-language']).then(res => {
          this.translations = res.data
        })
      }
    },
    getTranslated (key) {
      var translation = this.translations.filter(element => element.key === key)
      if (translation.length > 0) {
        return translation[0].translation
      } else {
        return 'Translation Error: ' + key
      }
    },
    errorManagement (e) {
      if (e.response !== undefined) {
        switch (e.response.status) {
          case 401:
            this.notify({ color: 'warning', message: 'Unauthorized error' })
            break
          case 422:
            this.notify({ color: 'warning', message: 'Unprocessable Entity error' })
            break
          case 500:
            this.notify({ color: 'warning', message: 'Internal server error' })
            break
          default:
            this.notify({ color: 'warning', message: 'Unexpected error' })
            break
        }
      } else {
        this.notify({ color: 'warning', message: 'Unexpected error' })
      }
    }
  },
  async created () {
    this.base_url = process.env.VUE_APP_SMARTDISPLAY_URL
    if (this.$root.isAuthenticated) {
      this.tenant = this.$root.$data.user.profile['customclaim-tenantname']
    }
    await this.initialize()
    this.tableLoading = false
  },
  beforeMount () {
    this.$store.state.smartDisplays.headersSmartDisplays = Object.assign([])
  }
}
</script>

<style>
iframe {
  overflow:hidden;
}
.rotate-90 {
  -webkit-transform: rotate(90deg);
  -moz-transform: rotate(90deg);
  -ms-transform: rotate(90deg);
  -o-transform: rotate(90deg);
  transform: rotate(90deg);
}
</style>
